'use client';

import { useTranslations } from 'next-intl';
import { Eye, Pencil, QrCode, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PermissionGate } from '@/features/auth';
import type { Registration } from '../types';

interface RegistrationCompactRowProps {
  registration: Registration;
  onEdit: (item: Registration) => void;
  onDelete: (item: Registration) => void;
  onView: (item: Registration) => void;
  onQr: (item: Registration) => void;
}

const STAGE_STYLES: Record<string, string> = {
  registration:
    'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200',
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  ready_for_packing:
    'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

const STAGE_STATUS_STYLES: Record<string, string> = {
  creating:
    'bg-slate-100 text-slate-700 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200',
  returned_for_correction:
    'bg-rose-100 text-rose-800 hover:bg-rose-100 dark:bg-rose-900 dark:text-rose-200',
  returned_from_sticking:
    'bg-rose-100 text-rose-800 hover:bg-rose-100 dark:bg-rose-900 dark:text-rose-200'
};

const PRIORITY_STYLES: Record<string, string> = {
  low: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400',
  medium: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400',
  high: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  urgent: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400'
};

export function RegistrationCompactRow({
  registration,
  onEdit,
  onDelete,
  onView,
  onQr
}: RegistrationCompactRowProps) {
  const t = useTranslations('registration');

  const stageLabels: Record<string, string> = {
    registration: t('stageRegistration'),
    sticking: t('stageSticking'),
    review: t('stageReview'),
    ready_for_packing: t('stageReadyForPacking'),
    completed: t('stageCompleted')
  };

  const priorityLabels: Record<string, string> = {
    low: t('priorityLow'),
    medium: t('priorityMedium'),
    high: t('priorityHigh'),
    urgent: t('priorityUrgent')
  };

  const stageStatusLabels: Record<string, string> = {
    creating: t('stageStatusCreating'),
    returned_for_correction: t('stageStatusReturnedForCorrection'),
    returned_from_sticking: t('stageStatusReturnedFromSticking')
  };

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: container number + entity name */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='font-mono font-medium'>
            {registration.container_number}
          </span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {registration.entity_name_ar}
          </span>
        </div>
      </div>

      {/* Center: priority badge (hidden on mobile) */}
      <Badge
        variant='outline'
        className={cn(
          'hidden shrink-0 text-xs uppercase md:inline-flex',
          PRIORITY_STYLES[registration.priority]
        )}
      >
        {priorityLabels[registration.priority] ?? registration.priority}
      </Badge>

      {/* Right: stage badge */}
      <Badge
        variant='secondary'
        className={cn(
          'shrink-0 text-xs',
          STAGE_STYLES[registration.current_stage]
        )}
      >
        {stageLabels[registration.current_stage] ?? registration.current_stage}
      </Badge>

      {/* Stage sub-status badge */}
      {registration.stage_status &&
        stageStatusLabels[registration.stage_status] && (
          <Badge
            variant='secondary'
            className={cn(
              'shrink-0 text-xs',
              STAGE_STATUS_STYLES[registration.stage_status]
            )}
          >
            {stageStatusLabels[registration.stage_status]}
          </Badge>
        )}

      {/* Action buttons */}
      <div className='flex shrink-0 items-center gap-1'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          onClick={() => onQr(registration)}
        >
          <QrCode className='h-3.5 w-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          onClick={() => onView(registration)}
        >
          <Eye className='h-3.5 w-3.5' />
        </Button>
        <PermissionGate permissions={['registrations.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(registration)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['registrations.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='text-destructive h-7 w-7'
            onClick={() => onDelete(registration)}
          >
            <Trash2 className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
